/**
 * Measurement preferences for shugert.com.mx.
 *
 * This site primarily serves Mexico and the United States, so analytics and
 * advertising measurement are enabled by default unless the visitor has made
 * an explicit choice. Existing choices are always preserved. Global Privacy
 * Control (GPC) overrides advertising measurement and keeps it disabled.
 */

export const CONSENT_V1_KEY = "consent.v1";
export const CONSENT_KEY = "consent.v2";
export const CONSENT_EVENT = "consent:change";
export const PRIVACY_SETTINGS_EVENT = "privacy:open";
export const PRIVACY_NOTICE_KEY = "privacy.notice.v1";

const GA4_ID = "G-LHD9X6Y8TP";

export type Consent = {
  analytics: boolean;
  ads: boolean;
  ts: number;
};

type ConsentWindow = Window & { __consent?: Consent };
type NavigatorWithGpc = Navigator & { globalPrivacyControl?: boolean };

function parse(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Current explicit v2 preference, or null when the visitor has not saved one. */
export function readConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const v2 = parse(window.localStorage.getItem(CONSENT_KEY));
    if (v2 && typeof v2.analytics === "boolean" && typeof v2.ads === "boolean") {
      return { analytics: v2.analytics, ads: v2.ads, ts: Number(v2.ts) || 0 };
    }
  } catch {
    /* storage unavailable */
  }
  return null;
}

/** Returns the legacy analytics decision, preserving false as an explicit opt-out. */
export function readLegacyAnalyticsDecision(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const v1 = parse(window.localStorage.getItem(CONSENT_V1_KEY));
    if (!v1 || typeof v1.analytics !== "boolean") return null;
    return v1.analytics;
  } catch {
    return null;
  }
}

/** Backward-compatible helper retained for callers that only need a boolean. */
export function readLegacyAnalyticsConsent(): boolean {
  return readLegacyAnalyticsDecision() === true;
}

export function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === "undefined") return false;
  return (navigator as NavigatorWithGpc).globalPrivacyControl === true;
}

/** Defaults used only when the visitor has never made an explicit choice. */
export function getDefaultConsent(): Consent {
  return {
    analytics: true,
    ads: !hasGlobalPrivacyControl(),
    ts: 0,
  };
}

export function hasAnalyticsConsent(): boolean {
  const current = readConsent();
  if (current) return current.analytics;

  const legacy = readLegacyAnalyticsDecision();
  if (legacy !== null) return legacy;

  return true;
}

export function hasAdsConsent(): boolean {
  if (hasGlobalPrivacyControl()) return false;

  const current = readConsent();
  if (current) return current.ads;

  // A legacy record only covered analytics. Never reinterpret it as an ad
  // measurement grant; those visitors can choose ads explicitly in v2.
  if (readLegacyAnalyticsDecision() !== null) return false;

  return true;
}

function publishConsent(value: Consent): void {
  (window as ConsentWindow).__consent = value;
  // Google documents this flag as a supported way to disable collection for
  // a specific GA property after the library has already been initialized.
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA4_ID}`] = !value.analytics;
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

export function writeConsent(next: { analytics: boolean; ads: boolean }): Consent {
  const value: Consent = {
    analytics: next.analytics,
    ads: hasGlobalPrivacyControl() ? false : next.ads,
    ts: Date.now(),
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
  publishConsent(value);
  return value;
}

/**
 * Publishes the effective decision for early-loading scripts such as GA4.
 * Publishing also wakes scripts that were waiting for the consent:change
 * event, so default analytics starts without requiring a button click.
 */
export function hydrateConsent(): void {
  if (typeof window === "undefined") return;

  const current = readConsent();
  if (current) {
    publishConsent({
      ...current,
      ads: hasGlobalPrivacyControl() ? false : current.ads,
    });
    return;
  }

  const legacy = readLegacyAnalyticsDecision();
  if (legacy !== null) {
    publishConsent({ analytics: legacy, ads: false, ts: 0 });
    return;
  }

  publishConsent(getDefaultConsent());
}

export function onConsentChange(handler: (consent: Consent) => void): () => void {
  const listener = (event: Event) => handler((event as CustomEvent<Consent>).detail);
  window.addEventListener(CONSENT_EVENT, listener);
  return () => window.removeEventListener(CONSENT_EVENT, listener);
}
